import { useState, useEffect } from "react";
import {
  Sheet,
  SheetContent,
} from "@/components/ui/sheet";
import { CheckCircle, CloseCircle, EmojiFunnySquare, Settings } from "@solar-icons/react"
import { Button } from "@/components/ui/button";
import {
  getCookieConsent,
  setCookieConsent,
  getCookiePreferences,
  setCookiePreferences,
  type CookiePreferences,
} from "@/utils/cookies";
import { CookiePreferencesSheet } from "./cookie-preferences-dialog";

interface CookieConsentSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CookieConsentDialog({ open, onOpenChange }: CookieConsentSheetProps) {
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>(getCookiePreferences());

  useEffect(() => {
    // Check if user has already given consent
    const hasConsented = getCookieConsent();
    if (hasConsented) {
      onOpenChange(false);
    }
  }, [onOpenChange]);

  const handleAcceptAll = () => {
    const allAccepted: CookiePreferences = {
      essential: true,
      analytics: true,
      marketing: true,
      functional: true,
    };
    setCookiePreferences(allAccepted);
    setCookieConsent(true);
    onOpenChange(false);
  };



  const handleRejectAll = () => {
    const onlyEssential: CookiePreferences = {
      essential: true,
      analytics: false,
      marketing: false,
      functional: false,
    };
    setCookiePreferences(onlyEssential);
    setCookieConsent(true);
    onOpenChange(false);
  };

  const handleCustomize = () => {
    setShowPreferences(true);
  };

  const handlePreferencesSave = (newPreferences: CookiePreferences) => {
    setPreferences(newPreferences);
    setCookiePreferences(newPreferences);
    setCookieConsent(true);
    setShowPreferences(false);
    onOpenChange(false);
  };

  if (showPreferences) {
    return (
      <CookiePreferencesSheet
        open={showPreferences}
        onOpenChange={setShowPreferences}
        preferences={preferences}
        onSave={handlePreferencesSave}
      />
    );
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-auto p-3 sm:p-4 rounded-none">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 max-w-7xl mx-auto">
          {/* Icon and Content */}
          <div className="flex items-start gap-2 sm:gap-3 flex-1 min-w-0">
            <div className="flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-full bg-primary/10 shrink-0">
              <EmojiFunnySquare className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm sm:text-base font-semibold mb-0.5">Cookie Preferences</h3>
              <p className="text-xs sm:text-sm text-muted-foreground">
                We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic.
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-row items-center gap-2 w-full sm:w-auto shrink-0">
            <Button
              variant="outline"
              onClick={handleRejectAll}
              size="sm"
              className="gap-1.5 text-xs sm:text-sm"
            >
              <CloseCircle className="h-3.5 w-3.5" />
              Reject All
            </Button>
            <Button
              variant="outline"
              onClick={handleCustomize}
              size="sm"
              className="gap-1.5 text-xs sm:text-sm"
            >
              <Settings className="h-3.5 w-3.5" />
              Customize
            </Button>
            <Button
              onClick={handleAcceptAll}
              size="sm"
              className="gap-1.5 text-xs sm:text-sm"
            >
              <CheckCircle className="h-3.5 w-3.5" />
              Accept All
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
